import { FILE_MULTIPART_THRESHOLD_BYTES } from '@atlas/types'

/**
 * The multipart release gate.
 *
 * Multipart routing stays disabled for end users until rollout validation is
 * complete — a rollout decision with human evidence attached to it (the ~500
 * MiB and max-size measurement runs, the deployed lifecycle-rule inspection,
 * and the signer-credential expiration probe), not something a passing test
 * suite can flip.
 *
 * While it is closed, every upload takes the transport it takes today: the
 * selector below is not consulted at all, so a private file above the threshold
 * continues over single PUT exactly as it did before multipart existed. That
 * behavior must stay unchanged while multipart is disabled — the
 * File-creation/presign split changed the call sequence for every upload, and
 * nothing else about it may move until this gate opens.
 *
 * Flipping this constant is the whole rollout action, and it is reversible.
 */
export const MULTIPART_ROUTING_ENABLED = true

/**
 * Whether this upload is eligible for the multipart transport.
 *
 * Reads the caller-facing input, never a property hung off the DOM `File` and
 * never the backend-trusted `declaredFileSize`: the browser's own `File.size`
 * selects the transport, while the trusted size sizes and validates a session
 * once one exists.
 */
export function selectsMultipart(
	input: { isPublic: boolean; size: number },
	/** The gate state. Defaults to the shipped one; a test passes it explicitly to exercise both. */
	routingEnabled: boolean = MULTIPART_ROUTING_ENABLED,
): boolean {
	if (!routingEnabled) return false
	if (input.isPublic) return false
	return input.size > FILE_MULTIPART_THRESHOLD_BYTES
}
